import {
  Control,
  FieldValues,
  Path,
  PathValue,
  UseFormGetValues,
  UseFormSetValue,
  useWatch,
} from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { makeStyles } from "tss-react/mui";
import { MiControlledTextField, MiIcon } from "@miplatform/components";
import { TYPOGRAPHY_VARIANTS } from "@miplatform/constants";
import { IMaterialsResponse } from "@miplatform/types";
import { mdiChevronDown } from "@mdi/js";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from "@mui/material";
import { MaterialPricingRow } from "./MaterialPricingRow";

export function CategoryPricingAccordion<T extends FieldValues>({
  categoryName,
  fieldName,
  fieldLabel,
  formControl,
  setValue,
  getValues,
  valueType,
  disabled = false,
  children,
  materials = [],
  categoryId,
  search = "",
}: Readonly<CategoryPricingAccordionProps<T>>) {
  const { classes, theme } = useStyles();

  const watchedMaterials = useWatch({
    control: formControl,
    name: `itemPricingByCategory.${categoryId}` as Path<T>,
  }) as IMaterialsResponse[] | undefined;

  const modifierValue = useWatch({
    control: formControl,
    name: fieldName,
  }) as number | null | undefined;

  return (
    <Accordion defaultExpanded disableGutters className={classes.accordion}>
      <AccordionSummary
        expandIcon={<MiIcon path={mdiChevronDown} />}
        className={classes.accordionSummary}
      >
        <Box className={classes.headerContent}>
          <Typography variant={TYPOGRAPHY_VARIANTS.HD2}>
            {categoryName} Category
          </Typography>
          <Box
            className={classes.inputContainer}
            onClick={(e) => e.stopPropagation()}
          >
            <NumericFormat
              customInput={MiControlledTextField}
              name={fieldName}
              formControl={formControl}
              label={fieldLabel}
              value={getValues(fieldName) ?? ""}
              decimalScale={2}
              fixedDecimalScale
              allowNegative
              suffix={valueType === "markup" ? "%" : undefined}
              disabled={disabled}
              onValueChange={({ floatValue }, sourceInfo) => {
                if (sourceInfo.source !== "event") return;

                setValue(
                  fieldName,
                  (floatValue ?? null) as PathValue<T, Path<T>>,
                  {
                    shouldValidate: true,
                    shouldDirty: true,
                  }
                );
              }}
            />
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails className={classes.accordionDetails}>
        <Box className={classes.materialsContainer}>
          {materials.length > 0 ? (
            materials.map((material, materialIndex) => {
              const basePath = `itemPricingByCategory.${categoryId}.${materialIndex}`;
              const defaultValue = valueType === "multiplier" ? 1 : 0;

              return (
                <MaterialPricingRow<T>
                  key={material.id}
                  material={material}
                  watched={watchedMaterials?.[materialIndex]}
                  modifierValue={modifierValue ?? defaultValue}
                  valueType={valueType}
                  basePath={basePath}
                  formControl={formControl}
                  setValue={setValue}
                  disabled={disabled}
                  search={search}
                />
              );
            })
          ) : (
            <Typography
              variant={TYPOGRAPHY_VARIANTS.BODY2}
              color={theme.palette.text.secondary}
              className={classes.emptyText}
            >
              {search
                ? `No materials found.`
                : "No materials assigned to this category."}
            </Typography>
          )}
        </Box>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

const useStyles = makeStyles()((theme) => ({
  accordion: {
    border: `1px solid ${theme.palette.grey.lightAccent}`,
    backgroundColor: theme.palette.grey.light,
    borderRadius: 2,
    boxShadow: "none",
    "&:before": {
      display: "none",
    },
    "&.Mui-focusVisible": {
      backgroundColor: theme.palette.grey.light,
    },
  },
  accordionSummary: {
    minHeight: 0,
    padding: theme.spacing(0.5, 2),
    backgroundColor: theme.palette.grey.mediumLight,
    "& .MuiAccordionSummary-content": {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      margin: 0,
    },
    "& .MuiAccordionSummary-content.Mui-expanded": {
      margin: 0,
    },
    "&.Mui-expanded": {
      borderBottom: `1px solid ${theme.palette.grey.lightAccent}`,
      minHeight: 0,
    },
    "&.Mui-focusVisible": {
      backgroundColor: theme.palette.grey.mediumLight,
    },
  },
  headerContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    paddingRight: theme.spacing(1),
  },
  inputContainer: {
    width: 150,
    "& .MuiInputBase-root": {
      backgroundColor: theme.palette.background.paper,
    },
  },
  accordionDetails: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
  },
  materialsContainer: {
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${theme.palette.grey.lightAccent}`,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 4,
    overflowY: "auto",
  },
  emptyText: {
    margin: theme.spacing(4),
    alignSelf: "center",
  },
}));

interface CategoryPricingAccordionProps<T extends FieldValues> {
  categoryName: string;
  categoryId: string;
  fieldName: Path<T>;
  fieldLabel: string;
  formControl: Control<T>;
  setValue: UseFormSetValue<T>;
  getValues: UseFormGetValues<T>;
  valueType: "multiplier" | "markup";
  disabled?: boolean;
  children?: React.ReactNode;
  materials?: IMaterialsResponse[];
  search?: string;
}
